import { type SASS } from "@/models/stff";
import { type SCLR } from "@/models";
import { type RelatedGroup } from "@/components/shared/BlockedDeleteDialog";
import {
  makeSassId,
  makeSclrId,
} from "@/components/records/staff/hooks/useBlockedStaffDeletion";
import SASSEditDialog from "@/components/records/staff/dialogs/SASSEditDialog";
import SCLREditDialog from "@/components/records/staff/dialogs/SCLREditDialog";

export type SASSWithDisplay = SASS & {
  PreschoolName?: string;
  ClassroomRole1Name?: string;
  NonClassroomRole1Name?: string;
};

export type SCLRWithDisplay = SCLR & {
  PreschoolName?: string;
  ClassroomName?: string;
};

export function getStaffBlockedGroups({
  sassRecords,
  sclrRecords,
  pendingSassDeletes,
  pendingSclrDeletes,
  onSassDeleted,
  onSclrDeleted,
}: {
  sassRecords: SASSWithDisplay[];
  sclrRecords: SCLRWithDisplay[];
  pendingSassDeletes: Set<string>;
  pendingSclrDeletes: Set<string>;
  onSassDeleted: (r: SASS) => void;
  onSclrDeleted: (r: SCLR) => void;
}): RelatedGroup[] {
  return [
    {
      key: "sass",
      label: "Staff Assignment (SASS)",
      records: sassRecords
        .filter((s) => !pendingSassDeletes.has(makeSassId(s)))
        .map((s) => {
          const sRecord = s as unknown as Record<string, unknown>;
          const classroomRoleCount = Object.keys(sRecord).filter(
            (k) => /^ClassroomRole\d+$/.test(k) && sRecord[k]
          ).length;
          const nonClassroomRoleCount = Object.keys(sRecord).filter(
            (k) => /^NonClassroomRole\d+$/.test(k) && sRecord[k]
          ).length;
          const totalRoles = classroomRoleCount + nonClassroomRoleCount;

          const roleName =
            s.ClassroomRole1Name ?? s.NonClassroomRole1Name ?? "";
          const primaryParts = [
            s.PreschoolName,
            roleName,
            s.ProgramYear,
          ].filter(Boolean);

          let secondary: string | undefined;
          if (totalRoles === 1) {
            if (classroomRoleCount === 1 && s.ClassroomRole1Name) {
              secondary = `${s.ClassroomRole1Name}${
                s.ClassroomRole1PercentOfTime != null
                  ? ` · ${s.ClassroomRole1PercentOfTime}%`
                  : ""
              }`;
            } else if (s.NonClassroomRole1Name) {
              secondary = `${s.NonClassroomRole1Name}${
                s.NonClassroomRole1PercentOfTime != null
                  ? ` · ${s.NonClassroomRole1PercentOfTime}%`
                  : ""
              }`;
            }
          } else if (totalRoles > 1) {
            const parts: string[] = [];
            if (classroomRoleCount > 0) {
              parts.push(
                `${classroomRoleCount} classroom role${classroomRoleCount !== 1 ? "s" : ""}`
              );
            }
            if (nonClassroomRoleCount > 0) {
              parts.push(
                `${nonClassroomRoleCount} non-classroom role${nonClassroomRoleCount !== 1 ? "s" : ""}`
              );
            }
            secondary = parts.join(" · ");
          }

          return {
            id: makeSassId(s),
            primary: primaryParts.join(" · "),
            secondary,
            data: s,
          };
        }),
      renderView: (record, onClose) => (
        <SASSEditDialog
          record={record as SASS}
          onClose={onClose}
          onDeleted={onSassDeleted}
          viewOnly
        />
      ),
    },
    {
      key: "sclr",
      label: "Staff Classroom (SCLR)",
      records: sclrRecords
        .filter((s) => !pendingSclrDeletes.has(makeSclrId(s)))
        .map((s) => {
          const primaryParts = [
            s.PreschoolName,
            s.ClassroomName,
            s.ProgramYear,
          ].filter(Boolean);
          return {
            id: makeSclrId(s),
            primary: primaryParts.join(" · "),
            secondary: s.LocalClassroomId
              ? `Classroom ID: ${s.LocalClassroomId}`
              : undefined,
            data: s,
          };
        }),
      renderView: (record, onClose) => (
        <SCLREditDialog
          record={record as SCLR}
          onClose={onClose}
          onDeleted={onSclrDeleted}
          viewOnly
        />
      ),
    },
  ];
}
